import { useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";


interface DataItem {
    name: string;
    value: number;
}

type CustomPieChartProps = {
    data: DataItem[];
    title: string;
    legendsFor: string;
}


export const CustomPieChart = ({ data, title, legendsFor }: CustomPieChartProps) => {
    const total = data?.reduce((sum, item) => sum + item.value, 0) || 0;
    const [hovered, setHovered] = useState<number | null>(null);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
    const printContentRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [width, setWidth] = useState<number>(0);
    const scheduleFullScreenRef = useRef<HTMLDivElement>(null);
    const [scheduleFullScreen, setScheduleFullScreen] = useState<boolean>(false);

    const randomColors = useMemo(
        () =>
            data?.map(() => `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`),
        [data]
    );

    useEffect(() => {
        const onResize = () => {
            if (scheduleFullScreenRef.current && !scheduleFullScreen) {
                setWidth(scheduleFullScreenRef.current?.clientWidth);
            }
        }

        onResize();
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, scheduleFullScreen])

    useEffect(() => {
        // Handler for fullscreen change event
        const handleFullscreenChange = () => {
            setScheduleFullScreen(document.fullscreenElement === scheduleFullScreenRef.current);
        };

        // Add event listeners for fullscreen change
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        // Clean up event listeners on component unmount
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };


    const getCoordinates = (value: number, total: number, radius: number) => {
        const angle = (value / total) * 2 * Math.PI - Math.PI / 2;
        return [Math.cos(angle) * radius, Math.sin(angle) * radius];
    };

    let cumulativeValue = 0;

    const handleFullScreen = () => {
        if (scheduleFullScreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        } else {
            const element = scheduleFullScreenRef.current;
            if (element) {
                element.requestFullscreen();
            }
        }
        setAnchorEl(null);
    }

    const handlePrint = () => {
        if (!printContentRef.current) return;
        const printContent = printContentRef.current;

        if (printContent) {
            const printWindow = window.open('', '', 'height=500, width=500');
            if (printWindow) {
                printWindow.document.documentElement.innerHTML = `
                <html>
                    <head>
                        <title>Express Tire Works</title>
                         <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: sans-serif;
        }
        .print-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-direction: row;
            gap: 20px;
        }
        svg {
            max-width: 60%;
            height: auto;
        }
        ul {
            max-height: none !important;
            overflow: visible !important;
            width: 40%;
        }
        @media print {
            body {
                break-inside: avoid;
            }
        }
             * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .color-dot {
        width: 12px;
        height: 12px;
        border-radius: 9999px;
        display: inline-block;
        margin-top: 4px;
      }
    </style>
                    </head>
                    <body>
                        ${printContent.innerHTML}
                    </body>
                </html>
            `;
                printWindow.document.close();
                printWindow.print();

                if (printWindow.closed) {
                    const element = scheduleFullScreenRef.current;
                    if (element) {
                        element.requestFullscreen();
                    }
                }
            }
        }
        setAnchorEl(null);
    }

    return (
        <div
            ref={scheduleFullScreenRef}
            className={`w-[49%] h-[372px] py-[10px] ml-[0.5rem] mt-[1.5rem] flex flex-col gap-1.5 bg-white border border-[#E4E7EC] rounded-[16px] relative`}>
            <div className="flex justify-between items-center px-[20px]">
                <h2 className={`text-lg font-semibold text-[#1D2939]`}>
                    {title}
                </h2>
                {
                    !scheduleFullScreen && (
                        <>
                            <button
                                className={`flex items-center justify-between py-[5px] px-[6px] gap-1.5 rounded-lg border border-solid border-[#E4E7EC]`}
                                aria-controls={open ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                onClick={handleClick}
                            >
                                <span className={`material-symbols-outlined text-[18px] font-light text-[#1D2939]`}>
                                    menu
                                </span>
                            </button>

                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            width: 170,
                                            backgroundColor: "#ffffff"
                                        }
                                    }
                                }}
                            >

                                <MenuItem
                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                    onClick={() => {
                                        handleFullScreen();
                                    }}>

                                    <span className={`text-black-rgba text-[16px] font-medium`}>View FullScreen</span>

                                </MenuItem>
                                <MenuItem
                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                    onClick={() => {
                                        handlePrint()
                                    }}>

                                    <span className={`text-black-rgba text-[16px] font-medium`}>Print</span>

                                </MenuItem>

                            </Menu>
                        </>
                    )
                }

            </div>

            <div className="w-full h-full flex justify-between items-center relative" ref={printContentRef}>
                <div className={`${scheduleFullScreen ? "w-full h-full" : ""}`}>
                    <svg ref={svgRef} width="100%" height="100%" viewBox="-100 -100 200 200">
                        {data?.map((item, index) => {
                            const start = getCoordinates(cumulativeValue, total, 80);
                            cumulativeValue += item.value;
                            const end = getCoordinates(cumulativeValue, total, 80);
                            const largeArc = item.value / total > 0.5 ? 1 : 0;
                            const midAngle = ((cumulativeValue - item.value / 2) / total) * 2 * Math.PI - Math.PI / 2;
                            const tooltipX = Math.cos(midAngle) * 60;
                            const tooltipY = Math.sin(midAngle) * 60;

                            return (
                                <g key={index}>
                                    {
                                        item.value === total ? (
                                            <circle
                                                r="80"
                                                cx="0"
                                                cy="0"
                                                fill={randomColors[index]}
                                                className={`transition-all duration-200 cursor-pointer ${hovered !== null && hovered !== index ? "opacity-20" : "opacity-100"
                                                    }`}
                                                onMouseEnter={(e) => {
                                                    setHovered(index);



                                                    if (svgRef.current) {

                                                        setTooltip({
                                                            x: scheduleFullScreen ? e.clientX + 50 : tooltipX + 80,
                                                            y: scheduleFullScreen ? e.clientY + 50 : tooltipY + 80,
                                                            text: `${item.name}: ${item.value} ${legendsFor}`,
                                                        });
                                                    }
                                                }}
                                                onMouseLeave={() => {
                                                    setHovered(null);
                                                    setTooltip(null);
                                                }}
                                            />
                                        )
                                            : (
                                                <path
                                                    d={`M0,0 L${start[0]},${start[1]} A80,80 0 ${largeArc},1 ${end[0]},${end[1]} Z`}
                                                    fill={randomColors[index]}
                                                    className={`transition-all duration-200 cursor-pointer ${hovered !== null && hovered !== index ? "opacity-20" : "opacity-100"
                                                        }`}
                                                    onMouseEnter={(e) => {
                                                        setHovered(index);


                                                        if (svgRef.current) {

                                                            setTooltip({
                                                                x: scheduleFullScreen ? e.clientX + 50 : tooltipX + 80,
                                                                y: scheduleFullScreen ? e.clientY + 50 : tooltipY + 80,
                                                                text: `${item.name}: ${item.value} ${legendsFor}`,
                                                            });
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHovered(null);
                                                        setTooltip(null);
                                                    }}
                                                />
                                            )
                                    }


                                </g>
                            );
                        })}
                    </svg>
                    {tooltip && (
                        <div
                            className="absolute bg-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap"
                            style={{ top: tooltip.y, left: tooltip.x, transform: "translate(-50%, -100%)" }}
                        >
                            {tooltip.text}
                        </div>
                    )}
                </div>

                <ul className="max-h-[300px] overflow-y-auto w-[200px]">
                    {data?.map((item, index) => (
                        <li key={index} className="flex items-start gap-1.5 py-2 px-1">
                            <div
                                className="color-dot w-2 h-2 rounded-full mt-[4px] inline-block"
                                style={{ backgroundColor: randomColors[index] }}
                            />
                            <div className="flex flex-col gap-1.5">
                                <span className={`text-xs font-semibold text-[#1D2939]`}>{item.name}</span>
                                <div className="w-full flex gap-1.5 items-center">
                                    <span className={`text-xs font-semibold text-[#1D2939]`}>{((total !== 0 ? item.value / total : 0) * 100).toFixed(1)}%</span>
                                    <span
                                        className="w-1 h-1 rounded-full bg-gray-500"
                                    />
                                    <span className={`text-xs text-black-rgba font-semibold`}>{item.value} {legendsFor}</span>
                                </div>
                            </div>

                        </li>
                    ))}
                </ul>


            </div>

        </div>

    )
}